import React, { useState } from 'react';
import { 
  ClipboardCheck, Shield, Microscope, PackageCheck, Factory, 
  FileCheck, ChevronRight, MapPin, Phone, Mail, ChevronDown,
  Download, Star, Building2, Car, Smartphone, Shirt, Coffee,
  Clock 
} from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';

function QualityInspectionPage() {
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Инспекционный контроль</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                качества товаров
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Профессиональная проверка качества и соответствия продукции на всех этапах производства
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid with Elevated Cards */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              Наши услуги
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Предлагаем полный спектр услуг по контролю качества продукции на всех этапах производства и отгрузки.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Factory className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Выезд на производство',
                  description: 'Проверка соответствия продукции требованиям заказчика и условиям контракта'
                },
                {
                  icon: <PackageCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Предотгрузочный контроль',
                  description: 'Проверка внешнего вида, функциональности, комплектности и упаковки'
                },
                {
                  icon: <FileCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Подробные отчёты',
                  description: 'Составление детальных отчётов с фото и видеоматериалами'
                }
              ].map((service, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{service.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section with Gradient Background */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Преимущества инспекционного контроля
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Shield className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Снижение рисков',
                description: 'Минимизация вероятности получения некачественного товара'
              },
              {
                icon: <ClipboardCheck className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Гарантия соответствия',
                description: 'Уверенность в соответствии продукции требованиям и ожиданиям'
              },
              {
                icon: <Microscope className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Детальная проверка',
                description: 'Тщательный контроль каждого аспекта производства и качества'
              },
              {
                icon: <Clock className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Экономия ресурсов',
                description: 'Своевременное выявление и устранение несоответствий'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-6">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Отрасли
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {[
              { icon: <Building2 className="w-8 h-8" />, name: 'Бытовые товары' },
              { icon: <Smartphone className="w-8 h-8" />, name: 'Электроника' },
              { icon: <Shirt className="w-8 h-8" />, name: 'Текстиль' },
              { icon: <Car className="w-8 h-8" />, name: 'Автозапчасти' },
              { icon: <Coffee className="w-8 h-8" />, name: 'Продукты питания' }
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="bg-[#1B3BA0]/5 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center text-[#1B3BA0]">
                  {industry.icon}
                </div>
                <p className="text-gray-700 font-medium">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Заказать инспекцию
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Оставьте заявку, и мы свяжемся с вами для обсуждения деталей проверки
          </p>
          <RequestForm source="Инспекционный контроль" />
        </div>
      </section>
    </div>
  );
}

export default QualityInspectionPage;